/**
 * @fileoverview Enhanced SEO meta component using react-helmet-async.
 * Supports Open Graph, Twitter Cards, canonical URLs, and JSON-LD structured data.
 * @module shared/components/common/PageMeta
 */

import { ReactElement, ReactNode } from 'react'
import { HelmetProvider, Helmet } from 'react-helmet-async'
import type { SupportedLanguage, HreflangAlternate } from './seoUtils'

const SITE_URL = 'https://abyssblock.com'
const SITE_NAME = 'abyssBlock'
const DEFAULT_OG_IMAGE = `${SITE_URL}/landing-page/og-image.png`

/**
 * JSON-LD structured data types
 */
interface JsonLdOrganization {
  '@type': 'Organization'
  name: string
  url: string
}

interface JsonLdWebPage {
  '@context': 'https://schema.org'
  '@type': 'WebPage'
  name: string
  description: string
  url: string
  publisher: JsonLdOrganization
}

interface JsonLdSoftwareApplication {
  '@context': 'https://schema.org'
  '@type': 'SoftwareApplication'
  name: string
  applicationCategory: string
  operatingSystem: string
  description: string
  url: string
  publisher: JsonLdOrganization
}

type JsonLdData = JsonLdWebPage | JsonLdSoftwareApplication

/**
 * PageMeta props for comprehensive SEO.
 */
interface PageMetaProps {
  /** Document title - will be appended with site name */
  title: string
  /** Meta description (recommended: 150-160 characters) */
  description: string
  /** Canonical URL path (e.g., '/signup'). Defaults to current path. */
  canonicalPath?: string
  /** Open Graph image URL. Defaults to site OG image. */
  ogImage?: string
  /** Open Graph type. Defaults to 'website'. */
  ogType?: 'website' | 'article' | 'product'
  /** Twitter card type. Defaults to 'summary_large_image'. */
  twitterCard?: 'summary' | 'summary_large_image'
  /** Disable indexing for this page */
  noIndex?: boolean
  /** Custom JSON-LD structured data */
  jsonLd?: JsonLdData
  /** Keywords for the page (optional, low SEO value but some engines use it) */
  keywords?: string[]
  /** Current page language for og:locale. Defaults to 'en'. */
  lang?: SupportedLanguage
  /** Hreflang alternate links for multi-language SEO */
  hreflang?: HreflangAlternate[]
}

/** Map language codes to og:locale format */
const LOCALE_MAP: Record<SupportedLanguage, string> = {
  en: 'en_US',
  pl: 'pl_PL',
}

/**
 * Enhanced page-level meta component with full SEO support.
 *
 * @component
 * @example
 * ```tsx
 * // Basic usage
 * <PageMeta
 *   title="Products"
 *   description="Manage your product catalog"
 * />
 *
 * // Full SEO with JSON-LD
 * <PageMeta
 *   title="Home"
 *   description="Track e-commerce profitability"
 *   canonicalPath="/"
 *   ogType="website"
 *   jsonLd={{
 *     '@context': 'https://schema.org',
 *     '@type': 'WebPage',
 *     name: 'AbyssBlock',
 *     description: 'E-commerce analytics',
 *     url: 'https://abyssblock.com',
 *     publisher: { '@type': 'Organization', name: 'AbyssBlock', url: 'https://abyssblock.com' }
 *   }}
 * />
 * ```
 */
export default function PageMeta({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noIndex = false,
  jsonLd,
  keywords,
  lang = 'en',
  hreflang,
}: PageMetaProps): ReactElement {
  const fullTitle = `${SITE_NAME} – ${title}`
  const canonicalUrl = canonicalPath ? `${SITE_URL}${canonicalPath}` : undefined
  const ogLocale = LOCALE_MAP[lang]

  return (
    <Helmet>
      {/* HTML lang attribute */}
      <html lang={lang} />

      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Hreflang alternate links */}
      {hreflang?.map(({ lang: hrefLang, href }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      {/* Alternate locales for OG */}
      {hreflang
        ?.filter(({ lang: l }) => l !== 'x-default' && l !== lang)
        .map(({ lang: altLang }) => (
          <meta
            key={`og-alt-${altLang}`}
            property="og:locale:alternate"
            content={LOCALE_MAP[altLang as SupportedLanguage]}
          />
        ))}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  )
}

/**
 * App wrapper providing Helmet context.
 *
 * @component
 * @example
 * ```tsx
 * <AppWrapper>{children}</AppWrapper>
 * ```
 */
export function AppWrapper({ children }: { children: ReactNode }): ReactElement {
  return <HelmetProvider>{children}</HelmetProvider>
}

/**
 * Re-export types for consumers.
 * For generateHreflang and SUPPORTED_LANGUAGES, import from './seoUtils' directly.
 */
export type {
  PageMetaProps,
  JsonLdData,
  JsonLdWebPage,
  JsonLdSoftwareApplication,
}
export type { HreflangAlternate, SupportedLanguage } from './seoUtils'
